'use memo';

import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Confetti, type ConfettiRef } from '@/components/ui/confetti';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { assertDealLedgerEnabled } from '../../-utils/assertDealLedgerEnabled';

const completedSchema = z.object({
  dealId: z.string(),
});

export const Route = createFileRoute('/_staff/deal/add/$direction/completed')({
  beforeLoad: assertDealLedgerEnabled,
  validateSearch: zodValidator(completedSchema),
  loaderDeps: ({ search }) => ({ search }),
  component: DealCompleted,
});

function DealCompleted() {
  const confettiRef = useRef<ConfettiRef>(null);
  const { session } = useAuthStore();
  const { t } = useTranslation(defaultNS, { keyPrefix: 'pages.dealLedger' });
  const { search } = Route.useLoaderDeps();
  const navigate = useNavigate();

  useEffect(() => {
    confettiRef.current?.fire();
  }, []);

  return (
    <VStack className="items-center justify-center gap-6">
      <Confetti ref={confettiRef} />

      <AttributeCard
        title={t('completedTitle')}
        className="w-2/4 p-4"
        actionButton={
          <ActionButton
            variant="secondary"
            onClick={() =>
              void navigate({
                to: '/deal/$dealId',
                params: { dealId: search.dealId },
              })
            }
          >
            {t('completedGoToDetail')}
          </ActionButton>
        }
      >
        <VStack>
          <Text variant="h3">{t('completedMessage')}</Text>
          <Spacer />
          <HStack className="items-center">
            <Icon iconName="SquareCheckBig" text={t('completedByLabel')} />
            <Spacer />
            <Avatar className="grid size-8 place-items-center">
              <AvatarImage src={session?.person.avatarUrl} />
              <AvatarFallback>
                <Icon iconName="UserRoundIcon" />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            {session?.person?.name}
          </HStack>
        </VStack>
      </AttributeCard>

      <ActionButton
        variant="outline"
        onClick={() => void navigate({ to: '/deal/add' })}
      >
        {t('completedRegisterAnother')}
      </ActionButton>
    </VStack>
  );
}
